import {useEffect, useRef, useState} from 'react'
import {useNavigate, useSearchParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Skeleton} from '@/components/ui/skeleton'
import {AuthSeal} from '@/components/auth-seal'
import {AuthShell} from '@/components/auth-shell'
import {useAuth} from '@/hooks/platform/use-auth'
import {useVerifyEmail} from '@/hooks/empresa/identidad/use-email-verification'
import {limpiarCredencialDeLaBarra} from '@/lib/url-redaction'

type State = 'verifying' | 'ok' | 'error'

/** Sin sesión: el token es la prueba. */
export function VerificarCorreoPage() {
  const {t} = useTranslation()
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const {user} = useAuth()
  const verify = useVerifyEmail()
  const [state, setState] = useState<State>('verifying')
  const ran = useRef(false)
  // Se lee una vez y sale de la barra de direcciones: es canjeable por una sesión.
  const [token] = useState(() => params.get('token'))

  useEffect(() => {
    if (ran.current) return
    ran.current = true
    limpiarCredencialDeLaBarra()
    if (!token) {
      setState('error')
      return
    }
    verify
      .mutateAsync(token)
      .then(() => setState('ok'))
      .catch(() => setState('error'))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <AuthShell>
      {state === 'verifying' && (
        <div className="space-y-4" data-testid="verify-email-loading">
          <h1 className="font-heading text-2xl tracking-tight text-foreground">{t('emailVerify.pageTitle')}</h1>
          <Skeleton className="h-8 w-full" />
        </div>
      )}
      {state === 'ok' && (
        <div className="space-y-4" data-testid="verify-email-ok">
          <AuthSeal />
          <h1 className="font-heading text-2xl tracking-tight text-foreground">{t('emailVerify.pageSuccess')}</h1>
          <Button
            className="w-full"
            onClick={() => navigate(user ? '/' : '/login', {replace: true})}
            data-testid="verify-email-continue">
            {t(user ? 'emailVerify.goToApp' : 'emailVerify.backToLogin')}
          </Button>
        </div>
      )}
      {state === 'error' && (
        <div className="space-y-4" data-testid="verify-email-error">
          <h1 className="notice-red rounded-lg px-3.5 py-3 font-heading text-lg tracking-tight">
            {t('emailVerify.pageError')}
          </h1>
          <Button
            className="w-full"
            onClick={() => navigate(user ? '/' : '/login', {replace: true})}
            data-testid="verify-email-continue">
            {t(user ? 'emailVerify.goToAppResend' : 'emailVerify.backToLogin')}
          </Button>
        </div>
      )}
    </AuthShell>
  )
}
